import { useCallback, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import type { ChapterImage, Manifest } from "../api";
import Player from "./Player";
import { useProgressWriter } from "./progressWriter";
import type { Speakers } from "./speakers";

/**
 * ChapterPlayer wires progress persistence around the player itself.
 *
 * Shared by the reader and the studio views, which differ in whether a long
 * press can correct a word, and in that only the reader's place is kept: the
 * studio plays the working copy, and progress is a place in the published one.
 */
export default function ChapterPlayer({
  chapterID,
  chapterNumber,
  bookID,
  manifest,
  allowCorrections = false,
  allowAsking = false,
  draft = false,
  onEditLine,
  allowRewrite = false,
  images,
  footer,
  title,
  speakers,
  onEnd,
  onListenerMove,
}: {
  chapterID: string;
  /** The chapter as the reader's list numbers it, for the chat (ADR-0176). */
  chapterNumber?: number;
  bookID?: string;
  manifest: Manifest;
  allowCorrections?: boolean;
  allowAsking?: boolean;
  /** Plays the working copy's audio rather than the published edition's. */
  draft?: boolean;
  /** The studio's way from a line to the editor (ADR-0025). */
  onEditLine?: (line: string, rewrite: boolean) => void;
  allowRewrite?: boolean;
  /** The chapter's pictures (ADR-0038), laid out among the spoken sentences. */
  images?: ChapterImage[];
  /** What follows the chapter's text, above the room left for the transport. */
  footer?: ReactNode;
  /** What the lock screen calls this chapter. */
  title?: string;
  /** Who says each quote (ADR-0125), the studio's. */
  speakers?: Speakers | null;
  /** The chapter's audio reached its real end. */
  onEnd?: () => void;
  /** The listener pressed play or moved. */
  onListenerMove?: (move: "play" | "seek") => void;
}) {
  // The studio's draft audio is not where a reader is (ADR-0058).
  const { saved, settled, record: save } = useProgressWriter(chapterID, !draft);
  // A chapter auto-play went on into (ADR-0177) is heard from its beginning,
  // whatever place was saved in it before (the owner, 10-14). Read once: the
  // player clears the navigation's state as it starts.
  const location = useLocation();
  const [fromStart] = useState(() => !!(location.state as { autoplay?: boolean } | null)?.autoplay);
  const duration = manifest.duration_ms;
  const record = useCallback(
    (positionMs: number, completed: boolean) =>
      save({
        position_ms: Math.round(positionMs),
        fraction: duration ? positionMs / duration : 0,
        completed,
      }),
    [duration, save],
  );

  // The player takes its start once, so it waits for the saved place: a
  // chapter opened cold used to start at 0 because the place landed after it.
  if (!fromStart && !settled) return null;
  // A finished chapter opens at its start, as in the app: going back to one is
  // listening again.
  const startAtMs = fromStart || saved?.completed ? 0 : (saved?.position_ms ?? 0);

  return (
    <Player
      chapterID={chapterID}
      chapterNumber={chapterNumber}
      bookID={bookID}
      manifest={manifest}
      startAtMs={startAtMs}
      onPosition={record}
      allowCorrections={allowCorrections}
      allowAsking={allowAsking}
      draft={draft}
      onEditLine={onEditLine}
      allowRewrite={allowRewrite}
      images={images}
      footer={footer}
      title={title}
      speakers={speakers}
      onEnd={onEnd}
      onListenerMove={onListenerMove}
    />
  );
}
